/**
 * 周回する天体の設定。
 * 軌道半径はメイン惑星(z≈16)と星空球(r=27)の間の 19〜24 に収めて「空の物体」として見せる。
 * minTier: 'high' の天体は low tier 端末では生成しない。
 *
 * node / phase / zoneTheta は「軌道が TRIGGER_ZONE.direction を通過する」ように数値計算で求めた値。
 *   - zoneTheta: 天体がゾーン中心に最接近する公転角
 *   - phase: 最初のゾーン通過が moon≈25s / saturn≈80s / ember≈135s / aurora≈265s で
 *     起きるよう逆算（訪問者が離脱する前に最初のエフェクトを見せるため moon を早めに）
 * （軌道位置 = Ry(node) * Rx(inclination) * Ry(θ) * (orbitRadius, 0, 0)）
 *
 * surfaceStyle: 表面模様のスタイル（body シェーダーの SURFACE_STYLE define に対応）。
 *   'saturn'（細かい緯度縞）/ 'sun'（沸き立つ粒状斑）/ 'pearl'（真珠質の氷）/
 *   'auroraIce'（極オーロラ + 氷）。未指定は従来の 'bands'（sin 縞）。
 */
export const CELESTIAL_BODIES = [
  {
    name: 'moon',
    bodyRadius: 1.5,
    orbitRadius: 22,
    inclination: 0.45,  // 軌道面の傾き（rad）
    node: 5.16,         // 昇交点（軌道面の向き, rad）
    speed: 0.035,       // 軌道角速度（rad/s）。周期 ≈ 180s
    phase: 1.21,        // 初期位相（rad）。初回ゾーン通過 ≈ 25s
    zoneTheta: 2.09,    // ゾーン中心に最接近する公転角（rad）
    colorA: '#e9edff',  // 明るい側
    colorB: '#9aa8cc',  // 陰側
    haloColor: '#cdd9ff',
    haloScale: 4.5,
    bandFreq: 6.0,      // 表面模様のディテール周波数
    surfaceStyle: 'pearl',
    effect: 'moonRays',
    minTier: 'low',
  },
  {
    name: 'emberPlanet',
    bodyRadius: 0.9,
    orbitRadius: 24,
    inclination: -0.3,
    node: 2.59,
    speed: 0.022,       // 周期 ≈ 286s
    phase: 1.74,        // 初回ゾーン通過 ≈ 135s
    zoneTheta: 4.71,
    colorA: '#ffd9a0',
    colorB: '#c96a3b',
    haloColor: '#ffb46b',
    haloScale: 6.0,
    bandFreq: 9.0,
    surfaceStyle: 'sun',
    effect: 'godRays',
    minTier: 'low',
  },
  {
    name: 'saturn',
    bodyRadius: 1.3,
    orbitRadius: 23,
    inclination: 0.6,
    node: 0.38,
    speed: 0.030,       // 周期 ≈ 209s
    phase: 4.61,        // 初回ゾーン通過 ≈ 80s
    zoneTheta: 0.73,
    colorA: '#f5e3b8',  // クリーム〜タンの縞（土星）
    colorB: '#c9a06b',
    haloColor: '#ffe680',
    haloScale: 5.0,
    bandFreq: 2.0,      // 縞の本数（saturn スタイルでは floor で帯を刻む）
    surfaceStyle: 'saturn',
    effect: 'ringBurst',
    minTier: 'low',
    // 傾いたリング（カッシーニの間隙付き）
    rings: { innerScale: 1.5, outerScale: 2.7, tilt: 0.47, color: '#e8d5a8' },
    // 渦巻き銀河パーティクル（RingBurst）。色は debug GUI でも実行中に変更可能
    // colorOuter は黒 = 加算合成で外側ほど溶けて消える（金の粒だけが中心に残る見た目）
    galaxy: { enabled: true, colorInner: '#ffd27a', colorOuter: '#000000' },
    // リング面に沿って散らばる氷の粒（公転・リングの傾きに追従）
    // NOTE: いったん非表示にして様子見中。戻すときはコメントを外すだけ
    // ambientParticles: { count: 90, color: '#ffe9a8', size: 200, layout: 'ring' },
  },
  {
    name: 'auroraMoon',
    bodyRadius: 0.6,
    orbitRadius: 20,
    inclination: 0.8,
    node: 4.56,
    speed: 0.028,       // 周期 ≈ 224s
    phase: 1.45,        // 初回ゾーン通過 ≈ 265s
    zoneTheta: 2.59,
    colorA: '#c8ffe8',
    colorB: '#5cb890',
    haloColor: '#7dffc4',
    haloScale: 3.0,
    bandFreq: 5.0,
    surfaceStyle: 'auroraIce',
    effect: 'aurora',
    minTier: 'high',
  },
]

/**
 * トリガーゾーン（ワールド空間のコーン）。
 * デフォルトカメラ（(0,0,30) FOV35 から原点方向を見る）で「画面右上の空」に見える
 * 遠側スカイの方向（視軸から右へ約13°・上へ約10°）。
 * enter < exit のヒステリシスで境界のチラつきを防ぐ。
 */
export const TRIGGER_ZONE = {
  direction: [0.490, 0.377, -0.786],       // 正規化はマネージャ側で行う
  enterAngle: (18 * Math.PI) / 180,        // この角度以内に入ったら発動
  exitAngle: (24 * Math.PI) / 180,         // この角度を出たら解除
}

// エフェクト発動中に Halo Scale を広げる最大値（debug スライダーの上限と同じ）
export const HALO_ZONE_SCALE = 15

/**
 * オーロラのサブエフェクトの ON/OFF。
 * 今後採用しない可能性があるため、ここのフラグ1つで簡単に無効化できるようにしている。
 * （debug GUI の auroraMoon フォルダからも実行中に切り替え可能）
 */
export const AURORA_SUB_EFFECTS = {
  sceneTint: true,        // 発動中に情景全体（星空・太陽光・惑星グロー）をオーロラ色に染める
  fallingParticles: false, // 降り注ぐ光の粒
}
